/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import classnames from 'classnames';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';

import { Button, INodeProps, Waiting } from '@msdyn365-commerce-modules/utilities';
import { IWishlistActionSuccessResult } from '@msdyn365-commerce/components';
import { getUrlSync, IDictionary, IImageDimension, IImageSettings } from '@msdyn365-commerce/core';
import { ICartState } from '@msdyn365-commerce/global-state';
import { AttributeValue, CartLine, CommerceProperty, SimpleProduct } from '@msdyn365-commerce/retail-proxy';

import { SubscriptionCommerceValues } from '../../common/subscription-commerce.values';
import { publish } from '../../Utilities/analytics/analytics-dispatcher';
import CommerceAttributesParser from '../../Utilities/commerce-attributes-parser';
import { calculateSavingPercentAmount } from '../../Utilities/elicit-savings-calculation';
import { getAttributesForCart, IAttributeBank } from '../../Utilities/get-product-attributes';
import { filterCartLines, getSubscriptionValues, isLineSubscriptionLine, updateSubscription } from '../../Utilities/subscription-manager';
import { IOrderSummaryLines, OrderSummary } from './../../Utilities/smwe-order-summary-lineitem';

import { _assembleCartlines, ElicitCartLineItems, IElicitCartlinesViewProps } from './components/elicit-cart-line-items';
import { ElicitPromoCodeComponent } from './components/elicit-promo-code';
import { ElicitSubscriptionHeader } from './components/elicit-subscription-header';
import { ElicitSubscriptionUpsell } from './components/elicit-subscription-upsell';
import { IElicitCartData } from './elicit-cart.data';
import { IElicitCartProps } from './elicit-cart.props.autogenerated';

export interface IElicitCartViewProps extends IElicitCartProps<IElicitCartData> {
    className: string;
    title?: React.ReactNode;
    CartlinesWrapper: INodeProps;
    cartlines?: IElicitCartlinesViewProps[];
    cart: ICartState | undefined;
    orderSummaryHeading: React.ReactNode;
    orderSummaryLineitems?: IOrderSummaryLines;
    OrderSummaryWrapper: INodeProps;
    OrderSummaryItems: INodeProps;
    checkoutAsGuestButton: React.ReactNode;
    checkoutAsSignInUserButton: React.ReactNode;
    storeSelector?: React.ReactNode;
    backToShoppingButton: React.ReactNode;
    waitingComponent: React.ReactNode;
    promoCode: React.ReactNode;
    cartLoadingStatus: string;
    cartDataResult: boolean;

    checkoutButtonDisabled: React.ReactNode;
    cartLineStatus: IDictionary<boolean>;
    mixedCart: IDictionary<boolean>;

    subscriptionHeader: React.ReactNode;
    subscriptionUpsell: React.ReactNode;

    shouldShowCheckoutInline: boolean;

    removeItemClickHandler(cartlineToRemove: CartLine): void;
    moveToWishlistSuccessHandler(result: IWishlistActionSuccessResult, cartlineId: CartLine): void;
}

interface IImageSettingsViewports {
    xs: IImageDimension;
    sm?: IImageDimension | undefined;
    md?: IImageDimension | undefined;
    lg?: IImageDimension | undefined;
    xl?: IImageDimension | undefined;
}

/**
 *
 * Cart component
 * @extends {React.Component<IElicitCartProps<IElicitCartData>>}
 */
@observer
class ElicitCart extends React.Component<IElicitCartProps<IElicitCartData>> {

    private cartLinesInvalid: IDictionary<boolean> = {};
    private mixedCart: IDictionary<boolean> = {};

    @observable private productAttributesList: { [key: number]: AttributeValue[] } = {};
    private parsedProductAttributes: IAttributeBank = {};

    constructor(props: IElicitCartProps<IElicitCartData>) {
        super(props);
        this._updateSubscriptionName = this._updateSubscriptionName.bind(this);

        const modifiedImageSettings = props.config.imageSettings;
        if (modifiedImageSettings) {
            if (modifiedImageSettings.viewports) {
                modifiedImageSettings.viewports = this.usePNGImageSetting(modifiedImageSettings.viewports);
                props.config.imageSettings = modifiedImageSettings;
            }
        }
    }

    //----------------------------------------------------------
    //----------------------------------------------------------
    public componentDidMount(): void {
        if (this._displaySummary) {

            const { cart, products } = this.props.data;

            // Wait for all data to load (except attributes, which rely on product IDs)
            Promise.all([cart, products]).then(() => {

                // Fetch attributes, then generate the checkout event
                this._setProductAttributes().then(() => {
                    this._generateCheckoutAnalyticsEvent(cart.result!, products.result!);
                }).catch(e => this.props.telemetry.exception(e));
            })
                .catch((e) => this.props.telemetry.exception(e));
        }
    }

    //----------------------------------------------------------
    //----------------------------------------------------------
    // tslint:disable-next-line:cyclomatic-complexity max-func-body-length
    public render(): JSX.Element | null {
        const {
            data: {
                cart: { result: cart },
                wishlists: { result: wishlists }
            },
            context: {
                request: {
                    user: {
                        isAuthenticated,
                        signInUrl
                    },
                    channel: {
                        PickupDeliveryModeCode
                    } = { PickupDeliveryModeCode: undefined }
                },
                app: {
                    routes
                }
            },
            config: config,
            telemetry,
            resources
        } = this.props;

        const channelConfiguration = this.props.context && this.props.context.request && this.props.context.request.channel;
        const imageSettings = this._getImageSettings(config.imageSettings);
        const guestCheckoutUrl = getUrlSync('checkout', this.props.context.actionContext) || '';
        const backToShoppingUrl = routes.backToShopping && routes.backToShopping.destinationUrl ?
            routes.backToShopping.destinationUrl
            : getUrlSync('home', this.props.context.actionContext) || '';
        const checkoutURL = this._getCheckoutURL(isAuthenticated, guestCheckoutUrl, signInUrl || '');
        const isCartEmpty = cart && cart.cart.CartLines && this._contextualLines.length === 0;
        const isCartREALLYEmpty = cart && cart.cart.CartLines && cart.cart.CartLines.length === 0;
        const isAllCartEmpty = cart?.cart.CartLines?.length === 0;

        const elicitCartItemProps = {
            percentSaving: calculateSavingPercentAmount(this._contextualLines),
            templatePercentSavingText: resources.elicitCart_subscriptionLinePercentSavingText,
            templateSavingText: resources.elicitCart_subscriptionSavingText,
            cartlines: this._contextualLines,
            cartLineErrors: this.cartLinesInvalid,
            mixedCart: this.mixedCart,
            mixedCartLines: cart?.cart.CartLines || [],
            products: this.props.data.products.result,
            productAvailabilites: this.props.data.productAvailabilites.result,
            productDeliveryOptions: this.props.data.deliveryOptions.result,
            imageSettings: imageSettings,
            outOfStockThreshold: this.props.context.app.config.outOfStockThreshold || 10,
            isStockCheckEnabled: this.props.context.app.config.enableStockCheck || false,
            maxCartlineQuantity: this.props.context.app.config.maxQuantityForCartLineItem,
            gridSettings: this.props.context.request.gridSettings!,
            context: this.props.context,
            typeName: this.props.typeName,
            id: this.props.id,
            resources: {
                sizeString: resources.elicitCart__productDimensionTypeSize,
                colorString: resources.elicitCart__productDimensionTypeColor,
                configString: 'asd',
                styleString: resources.elicitCart__productDimensionTypeStyle,
                quantityDisplayString: resources.elicitCart__quantityDisplayText,
                inputQuantityAriaLabel: resources.elicitCart__inputQuantityAriaLabel,
                discountStringText: resources.elicitCart__discountStringText,

                originalPriceText: resources.elicitCart__originalPriceText,
                currentPriceText: resources.elicitCart__currentPriceText,
                shippingChargesText: resources.elicitCart__shippingCharges,
            },
            telemetry: telemetry,
            addToWishlistButtonText: resources.elicitCart__addToWishlistButtonText,
            removeFromWishlistButtonText: resources.elicitCart__removeFromWishlistButtonText,
            removeButtonText: resources.elicitCart__removeCartButtonText,
            shipItText: resources.elicitCart__shipInsteadDisplayText,
            pickitUpText: resources.elicitCart__pickItUpDisplayText,
            changeStoreText: resources.elicitCart__changeStoreDisplayText,
            storeSelectorStateManager: this.props.data.storeSelectorStateManager.result,
            cartState: this.props.data.cart.result,
            orgUnitLocations: this.props.data.orgUnitLocations.result,
            removeItemClickHandler: this._removeItemFromCartHandler,
            moveToWishlistSuccessHandler: this._moveToWishlistSuccessHandler,
            updateCartLinesQuantitySuccessHandler: this._updateCartlineQuantity,
            wishlists: wishlists,
            defaultWishlistName: resources.elicitCart__defaultWishlistName,
            pickupDeliveryModeCode: PickupDeliveryModeCode,
            showImages: this.props.config.showImages,
            invalidProductError: this.props.resources.elicitCart__invalidProductError,
            outOfStockError: this.props.resources.elicitCart__outOfStockError,
            zeroAvailabilityError: this.props.resources.elicitCart__zeroAvailabilityError,
            showDiscountPercent: this.props.config.showDiscountPercent,
            productAttributesList: this.productAttributesList
        };

        // this ensures that all errors are populated in cart
        // the error object gets indirectly populated by the method
        _assembleCartlines(
            this.props.data.cart.result?.cart.CartLines || [],
            this.props.data.products.result || [],
            {
                ...elicitCartItemProps,
                cartlines: this.props.data.cart.result?.cart.CartLines || []
            },
            this.props.data.cart.result
        );

        const viewProps = {
            ...this.props,
            className: classnames('msc-cart'),
            title: this._title,
            CartlinesWrapper: { className: 'msc-cart-line' },
            OrderSummaryWrapper: { className: 'msc-order-summary' },
            OrderSummaryItems: { className: 'msc-order-summary__items' },
            cartlines: this.props.data.products && this.props.data.products.result && cart && cart.cart.CartLines && this._contextualLines.length > 0 ?
                (
                    ElicitCartLineItems(elicitCartItemProps)
                ) : undefined,
            promoCode: cart && (
                <ElicitPromoCodeComponent
                    cart={cart}
                    promoCodeHeadingText={resources.elicitCart__promoCodeHeadingText}
                    promoPlaceholderText={resources.elicitCart__promoPlaceholderText}
                    promoCodeApplyButtonText={resources.elicitCart__promoCodeApplyButtonText}
                    appliedPromoCodeHeadingText={resources.elicitCart__appliedPromoCodeHeadingText}
                    appliedPromoCodeSubheadingText={resources.elicitCart__appliedPromoCodeSubheadingText}
                    collapseTimeOut={350}
                    removePromoAriaLabelFormat={resources.elicitCart__removePromoAriaLabelFormat}
                    removePromoText={resources.elicitCart__removePromoText}
                    invalidPromoCodeErrorText={resources.elicitCart__invalidPromoCodeErrorText}
                    failedToAddPromoCodeErrorText={resources.elicitCart__failedToAddPromoCodeErrorText}
                    failedToRemovePromoCodeErrorText={resources.elicitCart__failedToRemovePromoCodeErrorText}
                    duplicatePromoCodeErrorText={resources.elicitCart__duplicatePromotionErrorText}
                    context={this.props.context}
                    id={this.props.id}
                    typeName={this.props.typeName}
                    data={{}}
                />
            ),

            cart: this.props.data.cart.result,
            context: this.props.context,
            typeName: this.props.typeName,
            telemetry: this.props.telemetry,
            id: this.props.id,
            removePromoAriaLabelFormat: resources.elicitCart__removePromoAriaLabelFormat,
            orderSummaryHeading: this.props.data.products && this.props.data.products.result && cart && cart.cart.CartLines && this._displaySummary && cart.cart.CartLines.length > 0 ?
                (
                    <h2 className={'msc-order-summary__heading'}>{resources.elicitCart__orderSummaryTitle}</h2>
                ) : null,
            orderSummaryLineitems: cart && cart.cart && channelConfiguration && cart.cart.CartLines && this._displaySummary && cart.cart.CartLines.length > 0 ?
                (
                    OrderSummary({
                        cart: cart.cart,
                        shipmentWarningText: this.props.config.shipmentWarningText as string,
                        subTotalLabel: resources.elicitCart__itemsWithCountLabel,
                        shippingLabel: resources.elicitCart__estimatedShippingLabel,
                        taxLabel: resources.elicitCart__estimatedTaxLabel,
                        orderTotalLabel: resources.elicitCart__orderTotal,
                        totalDiscountsLabel: resources.elicitCart__totalDiscountsLabel,
                        context: this.props.context,
                        typeName: this.props.typeName,
                        id: this.props.id,
                        toBeCalculatedText: resources.elicitCart__yetToBeCalculatedText,
                        freeText: resources.elicitCart__priceFree,
                        channelConfiguration: channelConfiguration,
                        telemetry: telemetry
                    })
                ) :
                (
                    undefined
                ),
            checkoutAsSignInUserButton: (!isCartREALLYEmpty) ?
                (
                    <Button className={'msc-cart__btn-checkout'} href={checkoutURL} title={resources.elicitCart__checkoutButtonTitle} onClick={this._updateSubscriptionName}>{resources.elicitCart__checkoutButtonTitle}</Button>
                ) : null,
            checkoutAsGuestButton: (!isCartREALLYEmpty && !isAuthenticated) ?
                (
                    <Button className={'msc-cart__btn-checkout-guest'} href={guestCheckoutUrl} title={resources.elicitCart__guestCheckoutButtonTitle}>{resources.elicitCart__guestCheckoutButtonTitle}</Button>
                ) : null,
            backToShoppingButton: <Button className={'msc-cart__btn-back'} href={backToShoppingUrl} title={resources.elicitCart__backToShoppingButtonTitle}>{resources.elicitCart__backToShoppingButtonTitle}</Button>,
            storeSelector: this.props.slots.storeSelector && this.props.slots.storeSelector.length > 0 ? this.props.slots.storeSelector[0] : null,
            removeItemClickHandler: this._removeItemFromCartHandler,
            moveToWishlistSuccessHandler: this._moveToWishlistSuccessHandler,
            waitingComponent: <Waiting className='msc-waiting-circular msc-waiting-lg' />,
            cartLoadingStatus: this.props.data.cart.status === 'FAILED' ? this.props.resources.elicitCart__cartLoadingErrorMessage : null,
            cartDataResult: !this._useForSubscriptions && this.props.data.cart.result && isAllCartEmpty,

            // DG: Additions to disable checkout button if an error is detected
            checkoutButtonDisabled: (
                <Button className={'msc-cart__btn-checkout'} href={checkoutURL} disabled={true} title={resources.elicitCart__checkoutButtonTitle}>{resources.elicitCart__checkoutButtonTitle}</Button>
            ),
            cartLineStatus: this.cartLinesInvalid,
            mixedCart: this.mixedCart,
            subscriptionHeader: this._useForSubscriptions && !isCartEmpty && (
                <ElicitSubscriptionHeader
                    cart={this.props.data.cart}
                    className='cart-subscription-header'
                    context={this.props.context}
                    frequencyTable={this.props.config.frequencyTable!}
                    editSubscriptionLink={this.props.config.editSubscriptionLink}
                    resources={{
                        deliveryText: resources.elicitCart_subscriptionDeliveryText,
                        savingText: resources.elicitCart_subscriptionSavingText,
                        savingLabel: resources.elicitCart_subscriptionSavingLabel,
                        wineBottleSummaryCount: resources.elicitCart_subscriptionWineBottleSummaryCount,
                        boxNamePlaceholder: resources.elicitCart__editBoxNamePlaceholder
                    }}
                />
            ),
            subscriptionUpsell: this._useForSubscriptions && !isCartEmpty && (
                <ElicitSubscriptionUpsell
                    cart={this.props.data.cart}
                    editSubscriptionLink={this.props.config.editSubscriptionLink}
                />
            ),
            shouldShowCheckoutInline: this._shouldShowCheckoutInline
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    public usePNGImageSetting = (viewports: IImageSettingsViewports): IImageSettingsViewports => {
        const modifiedViewports = viewports;
        if (modifiedViewports?.xs?.q) {
            modifiedViewports.xs.q = modifiedViewports.xs.q.replace('f=jpg', 'f=png');
        }
        if (modifiedViewports?.sm?.q) {
            modifiedViewports.sm.q = modifiedViewports.sm.q.replace('f=jpg', 'f=png');
        }
        if (modifiedViewports?.md?.q) {
            modifiedViewports.md.q = modifiedViewports.md.q.replace('f=jpg', 'f=png');
        }
        if (modifiedViewports?.lg?.q) {
            modifiedViewports.lg.q = modifiedViewports.lg.q.replace('f=jpg', 'f=png');
        }
        if (modifiedViewports?.xl?.q) {
            modifiedViewports.xl.q = modifiedViewports.xl.q.replace('f=jpg', 'f=png');
        }

        return modifiedViewports;
    };

    private get _shouldShowCheckoutInline(): boolean {

        const lines = filterCartLines(this.props.data.cart.result?.cart);

        if (this._useForSubscriptions) {
            // if theres sub items, show
            if (lines.subscriptionLines.length > 0) {
                return true;
            }
            return false;
        } else {
            // if only non sub items, show
            if (lines.subscriptionLines.length === 0 && lines.lines.length > 0) {
                return true;
            }
            return false;
        }
    }

    private _getCheckoutURL(isAuthenticated: boolean, guestCheckoutUrl: string, signInUrl: string): string {
        if (isAuthenticated) {
            return guestCheckoutUrl;
        }

        const absoluteUrlRegExp = new RegExp('^(?:[a-z]+:)?//', 'i');
        const isAbsoluteUrl = absoluteUrlRegExp.test(guestCheckoutUrl);
        let returnURL = guestCheckoutUrl;

        // tslint:disable-next-line: no-typeof-undefined
        if ((typeof window !== 'undefined') && !isAbsoluteUrl) {
            returnURL = `${window.location.origin}${guestCheckoutUrl}`;
        }

        return `${signInUrl}${signInUrl.indexOf('?') === -1 ? '?' : '&'}ru=${returnURL}`;
    }

    private _getImageSettings = (imageSettings: IImageSettings | undefined): IImageSettings => {
        return imageSettings || {
            viewports: {
                xs: { q: `w=80&h=94&m=6`, w: 0, h: 0 },
                sm: { q: `w=148&h=174&m=6`, w: 0, h: 0 },
                lg: { q: `w=148&h=174&m=6`, w: 0, h: 0 }
            },
            lazyload: true
        };
    };

    /**
     * contextual line can be either only subscription lines or only item lines
     */
    private get _contextualLines(): CartLine[] {
        return this.props.data.cart.result?.cart.CartLines?.filter(line => {
            const isSubLine = isLineSubscriptionLine(line);
            if (this._useForSubscriptions) {
                return isSubLine;
            } else {
                return !isSubLine;
            }
        }) || [];
    }

    private get _useForSubscriptions(): boolean {
        return !!this.props.config.useForSubscriptions;
    }

    private get _displaySummary(): boolean {
        return !!this.props.config.displaySummary;
    }

    private _removeItemFromCartHandler = (cartlineToRemove: CartLine) => {

        if (this.props.data.cart.result) {
            this._generateRemovalAnalyticsEvent(cartlineToRemove);

            delete this.cartLinesInvalid[cartlineToRemove.LineId!.toString()];

            const input = {
                cartLineIds: [cartlineToRemove.LineId!.toString()]
            };

            this.props.data.cart.result.removeCartLines(input)
                .catch((error) => {
                    this.props.telemetry.warning(error);
                    this.props.telemetry.debug('Unable to Remove Cart Line');
                });
        }
    };

    //----------------------------------------------------------
    //----------------------------------------------------------
    private _generateRemovalAnalyticsEvent(cartlineToRemove: CartLine): void {
        const productID = cartlineToRemove.ProductId;
        if (!productID) {
            return;
        }

        const product = this.props.data.products.result?.find(entry => entry.RecordId === productID);
        const isSubscription = isLineSubscriptionLine(cartlineToRemove);

        publish('removeFromCart', {
            product,
            quantity: cartlineToRemove.Quantity,
            attributes: this.parsedProductAttributes[productID],
            subscription: isSubscription,
            frequency: isSubscription && getSubscriptionValues(this.props.data.cart.result?.cart).frequency,
            context: this.props.context
        });
    }

    //----------------------------------------------------------
    //----------------------------------------------------------
    private _generateCheckoutAnalyticsEvent(cart: ICartState, products: SimpleProduct[]): void {

        const subFrequency = getSubscriptionValues(this.props.data.cart.result?.cart).frequency;

        // Convert to the format the analytics module requires
        const mappedProducts = cart.cart.CartLines?.map(line => {
            const product = products.find(entry => entry.RecordId === line.ProductId);
            const isSubscription = isLineSubscriptionLine(line);

            return {
                product,
                quantity: line.Quantity,
                attributes: this.parsedProductAttributes[line.ProductId!],
                subscription: isSubscription,
                frequency: isSubscription && subFrequency,
                discounts: line.DiscountLines,
                context: this.props.context,
            };
        });

        publish('checkout', {
            step: 0,     // We're counting the cart page as step 0
            products: mappedProducts,
            coupon: cart.cart.Coupons?.map(coupon => coupon.Code).join(','),
        });
    }

    private _moveToWishlistSuccessHandler = (result: IWishlistActionSuccessResult, cartline?: CartLine) => {
        if (result.status === 'ADDED' && cartline) {
            this._removeItemFromCartHandler(cartline);
        }
    };

    private _updateCartlineQuantity = (cartlineToUpdate: CartLine, quantity: number) => {
        if (this.props.data.cart.result) {
            delete this.cartLinesInvalid[cartlineToUpdate.LineId!.toString()];
            const input = {
                cartLineId: cartlineToUpdate.LineId!.toString(),
                newQuantity: quantity
            };
            this.props.data.cart.result.updateCartLineQuantity(input)
                .catch((error) => {
                    this.props.telemetry.warning(error);
                    this.props.telemetry.debug('Unable to update Cart Line quantity');
                });
        }
    };

    private async _setProductAttributes(): Promise<void> {
        const cart = this.props.data.cart?.result?.cart;
        if (cart && cart.CartLines) {
            this.parsedProductAttributes = await getAttributesForCart(cart?.CartLines, this.props.context.actionContext);

            Object.entries(this.parsedProductAttributes).forEach(([id, productAttribs]) => {
                this.productAttributesList[id] = CommerceAttributesParser.unParseAttributes(productAttribs);
            });
        }
    }

    private async _updateSubscriptionName(): Promise<void> {
        const values = getSubscriptionValues(this.props.data.cart.result?.cart);
        if (!values.name) {
            const cartChanges: CommerceProperty[] = [];
            cartChanges.push({
                Key: SubscriptionCommerceValues.SUBSCRIPTION_NAME,
                Value: {
                    StringValue: SubscriptionCommerceValues.SUBSCRIPTION_DEFAULT_NAME
                }
            });
            await updateSubscription(this.props.data.cart, cartChanges, this.props.context);
            return;
        }
    }

    private get _title(): JSX.Element | null {
        const { resources } = this.props;
        if (this._useForSubscriptions) {
            if (this._contextualLines.length === 0) { return null; }
            return <h1 className={'msc-cart__heading'}>{resources.elicitCart__subCartTitle}</h1>;
        }
        // special case where the subscription version has both
        if (this._contextualLines.length === 0 && filterCartLines(this.props.data.cart.result?.cart).subscriptionLines.length > 0) {
            return null;
        }
        return <h1 className={'msc-cart__heading'}>{resources.elicitCart__cartTitle}</h1>;
    }
}

export default ElicitCart;
